/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { MdSatellite, MdPower } from 'react-icons/md';
import ReactLoading from 'react-loading';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormControl, Select, MenuItem, InputLabel } from '@material-ui/core';

import LocaleMessage from '~/components/LocaleMessage';

import api from '~/services/pluginbot-api';

// Componentes estilizados que definem a estética premium do cartão
const CardContainer = styled.div`
    border-radius: 0.375rem;
    background: ${props => (props.theme.dark_mode ? '#424242' : '#fff')};
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.26);
    margin-bottom: 20px;
    padding: 20px;
    border-left: 4px solid ${props => props.theme.palette.primary.main};
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid
        ${props => (props.theme.dark_mode ? '#555' : '#eee')};
    padding-bottom: 12px;
    margin-bottom: 16px;

    h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 1.1rem;
        color: ${props => (props.theme.dark_mode ? '#fff' : '#333')};
    }
`;

const BatteryContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const BatteryOuter = styled.div`
    width: 48px;
    height: 22px;
    border: 2px solid ${props => (props.theme.dark_mode ? '#fff' : '#333')};
    border-radius: 4px;
    padding: 2px;
    position: relative;
    display: flex;

    &::after {
        content: '';
        position: absolute;
        right: -6px;
        top: 4px;
        width: 4px;
        height: 10px;
        background-color: ${props => (props.theme.dark_mode ? '#fff' : '#333')};
        border-radius: 0 2px 2px 0;
    }
`;

const BatteryLevel = styled.div`
    height: 100%;
    width: ${props => props.level}%;
    background-color: ${props => {
        if (props.level < 20) return '#f44336';
        if (props.level < 50) return '#ff9800';
        return '#4caf50';
    }};
    border-radius: 2px;
    transition: width 0.3s ease;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 0.9rem;
    color: ${props => (props.theme.dark_mode ? '#bbb' : '#666')};
    border-bottom: 1px dashed
        ${props => (props.theme.dark_mode ? '#444' : '#f5f5f5')};

    strong {
        color: ${props => (props.theme.dark_mode ? '#fff' : '#333')};
    }
`;

const DashboardBubble = styled.div`
    background: ${props => (props.theme.dark_mode ? '#2d2d2d' : '#f9f9f9')};
    border-radius: 6px;
    padding: 12px;
    text-align: center;
    border: 1px solid ${props => (props.theme.dark_mode ? '#444' : '#e0e0e0')};
`;

const LargeNumber = styled.div`
    font-size: 1.8rem;
    font-weight: bold;
    color: ${props => props.theme.palette.primary.main};
    line-height: 1;
    margin-bottom: 4px;
`;

/**
 * Renderiza a seção de cabeçalho do cartão de status do robô Pudu.
 * @returns {React.Component} Elemento JSX do cabeçalho.
 * @example
 * renderHeader()
 */
function renderHeader() {
    return (
        <CardHeader>
            <h3>
                <MdSatellite size={20} />
                <LocaleMessage msg="page.robots.pudu_status.title" />
            </h3>
        </CardHeader>
    );
}

/**
 * Renderiza o medidor visual de bateria e indicador de carregamento.
 * @param {number} battery - Porcentagem do nível de carga da bateria.
 * @param {boolean} charging - Verdadeiro se o robô estiver conectado na base de recarga.
 * @returns {React.Component} Elemento JSX do medidor de bateria.
 * @example
 * renderBattery(85, true)
 */
function renderBattery(battery, charging) {
    return (
        <BatteryContainer>
            <BatteryOuter>
                <BatteryLevel level={battery} />
            </BatteryOuter>
            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                {battery}%
            </span>
            {charging && (
                <MdPower
                    size={18}
                    color="#4caf50"
                    style={{ animation: 'pulse 1.2s infinite' }}
                />
            )}
        </BatteryContainer>
    );
}

/**
 * Renders basic robot network and identification properties.
 * @param {string} mac - MAC network hardware address.
 * @param {string} nickname - Pudu device nickname.
 * @param {string} model - Robot hardware model.
 * @returns {React.Component} Details grid JSX block.
 * @example
 * renderInfoGrid("aa:bb:cc:dd", "CC1-Robot", "CC1")
 */
/**
 * Formata um timestamp Unix (em segundos) em uma string de data e hora amigável.
 * @param {number|null} timestamp - Timestamp Unix em segundos.
 * @returns {string} String contendo a data e hora formatada.
 * @example
 * formatTimestamp(1778884167)
 */
function formatTimestamp(timestamp) {
    if (!timestamp) return '-';
    try {
        const d = new Date(timestamp * 1000);
        if (Number.isNaN(d.getTime())) return '-';
        const pad = val => String(val).padStart(2, '0');
        const dateStr = `${pad(d.getDate())}/${pad(
            d.getMonth() + 1
        )}/${d.getFullYear()}`;
        const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
            d.getSeconds()
        )}`;
        return `${dateStr} ${timeStr}`;
    } catch (err) {
        return '-';
    }
}

/**
 * Formata uma string de data e hora da Pudu em formato legível local.
 * @param {string|null} taskTimeStr - A data e hora em formato ISO ou YYYY-MM-DD HH:MM:SS.
 * @returns {string} Data e hora formatada como DD/MM/YYYY HH:MM:SS.
 * @example
 * formatTaskTime("2026-05-20 14:00:00")
 */
function formatTaskTime(taskTimeStr) {
    if (!taskTimeStr) return '-';
    try {
        const d = new Date(taskTimeStr.replace(/-/g, '/'));
        if (Number.isNaN(d.getTime())) return taskTimeStr;
        const pad = val => String(val).padStart(2, '0');
        const dateStr = `${pad(d.getDate())}/${pad(
            d.getMonth() + 1
        )}/${d.getFullYear()}`;
        const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
            d.getSeconds()
        )}`;
        return `${dateStr} ${timeStr}`;
    } catch (err) {
        return taskTimeStr;
    }
}

/**
 * Renderiza a grade de identificação básica, rede e última atividade do robô.
 * @param {string} mac - Endereço físico MAC do dispositivo de rede.
 * @param {string} model - Modelo de hardware do robô.
 * @param {number|null} lastOnlineTimestamp - Timestamp do último sinal ativo.
 * @param {Array} chargingHistory - Array contendo os logs históricos de carga do robô.
 * @returns {React.Component} Grade de detalhes do robô em formato JSX.
 * @example
 * renderInfoGrid("aa:bb:cc:dd", "CC1", 1778884167, [])
 */
function renderInfoGrid(mac, model, lastOnlineTimestamp, chargingHistory) {
    let lastChargeStr = '-';
    if (Array.isArray(chargingHistory) && chargingHistory.length > 0) {
        const lastCharge = chargingHistory[0];
        const formattedTime = formatTaskTime(lastCharge.task_time);
        const durationMin = lastCharge.charge_duration
            ? Math.floor(lastCharge.charge_duration / 60)
            : 0;
        const powerGain = lastCharge.charge_power_percent || 0;
        lastChargeStr = `${formattedTime} (${durationMin} min | +${powerGain}%)`;
    }

    return (
        <div style={{ marginTop: '10px' }}>
            <InfoRow>
                <span>
                    <LocaleMessage msg="page.robots.pudu_status.mac" />
                </span>
                <strong>{mac || '-'}</strong>
            </InfoRow>
            <InfoRow>
                <span>
                    <LocaleMessage msg="page.robots.pudu_status.model" />
                </span>
                <strong>{model || 'CC1'}</strong>
            </InfoRow>
            <InfoRow>
                <span>
                    <LocaleMessage msg="page.robots.pudu_status.last_online" />
                </span>
                <strong>{formatTimestamp(lastOnlineTimestamp)}</strong>
            </InfoRow>
            <InfoRow>
                <span>
                    <LocaleMessage msg="page.robots.pudu_status.last_charge" />
                </span>
                <strong>{lastChargeStr}</strong>
            </InfoRow>
        </div>
    );
}

/**
 * Renderiza a caixa de métricas de recarga com controle de período retroativo.
 * @param {number} chargeCount - A quantidade total calculada de operações de carga.
 * @param {number} days - Janela de tempo selecionada em dias.
 * @param {Function} handlePeriodChange - Função de retorno para atualização de estado do filtro.
 * @returns {React.Component} Painel de contagem em formato JSX.
 * @example
 * renderChargeCount(12, 30, (e) => {})
 */
function renderChargeCount(chargeCount, days, handlePeriodChange) {
    return (
        <DashboardBubble>
            <LargeNumber>{chargeCount}</LargeNumber>
            <div
                style={{
                    fontSize: '0.8rem',
                    marginBottom: '10px',
                    textTransform: 'uppercase',
                }}
            >
                <LocaleMessage msg="page.robots.pudu_status.charge_count" />
            </div>
            <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="pudu-period-label">
                    <LocaleMessage msg="page.robots.pudu_status.period" />
                </InputLabel>
                <Select
                    labelId="pudu-period-label"
                    value={days}
                    onChange={handlePeriodChange}
                    label={
                        <LocaleMessage msg="page.robots.pudu_status.period" />
                    }
                >
                    <MenuItem value={7}>
                        <LocaleMessage msg="page.robots.pudu_status.days_7" />
                    </MenuItem>
                    <MenuItem value={30}>
                        <LocaleMessage msg="page.robots.pudu_status.days_30" />
                    </MenuItem>
                    <MenuItem value={90}>
                        <LocaleMessage msg="page.robots.pudu_status.days_90" />
                    </MenuItem>
                    <MenuItem value={180}>
                        <LocaleMessage msg="page.robots.pudu_status.days_180" />
                    </MenuItem>
                </Select>
            </FormControl>
        </DashboardBubble>
    );
}

/**
 * Componente principal PuduStatusCard que exibe a conectividade, ciclos e detalhes físicos do robô.
 * @param {Object} props - Propriedades do componente React.
 * @param {string} props.robotId - O identificador único UID do robô ativo.
 * @returns {React.Component|null} O painel renderizado ou nulo se não houver credenciais extras.
 * @example
 * <PuduStatusCard robotId="abc-123" />
 */
export default function PuduStatusCard({ robotId }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [missingConfig, setMissingConfig] = useState(false);

    useEffect(() => {
        async function loadPuduStatus() {
            setLoading(true);
            try {
                const response = await api.get(
                    `/robots/${robotId}/pudu-status?days=${days}`
                );
                setData(response.data);
                setMissingConfig(false);
            } catch (err) {
                if (
                    err.response &&
                    err.response.data &&
                    err.response.data.code === 'robot_extras_not_found'
                ) {
                    setMissingConfig(true);
                }
            } finally {
                setLoading(false);
            }
        }

        if (robotId) {
            loadPuduStatus();
        }
    }, [robotId, days]);

    if (missingConfig) return null;

    return (
        <CardContainer>
            {renderHeader()}
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '20px',
                    }}
                >
                    <ReactLoading
                        type="spin"
                        color="#1a73e8"
                        height={30}
                        width={30}
                    />
                </div>
            ) : (
                data && (
                    <div className="row">
                        <div className="col-md-7 col-12">
                            {renderBattery(
                                data.battery,
                                data.is_charging === 1
                            )}
                            {renderInfoGrid(
                                data.mac,
                                'CC1',
                                data.last_online_timestamp,
                                data.charging_history
                            )}
                        </div>
                        <div className="col-md-5 col-12">
                            {renderChargeCount(data.charge_count, days, e =>
                                setDays(Number(e.target.value))
                            )}
                        </div>
                    </div>
                )
            )}
        </CardContainer>
    );
}

PuduStatusCard.propTypes = {
    robotId: PropTypes.string.isRequired,
};
